/**
 * LiveFusion PTT - Integrated with Mumble
 * Uses direct mumble-client for both desktop and mobile
 */

import { initRemoteLogging } from './remote-logger.js';
import { SimpleMumbleAudio } from './simple-mumble-audio.js';
import { GPSTracker } from './gps-tracker.js';
import { GPSMap } from './gps-map.js';
import { PTTSessionManager } from './session-manager.js';

class PTTIntegratedApp {
  constructor() {
    this.elements = {};
    this.isTransmitting = false;
    this.isPTTPressed = false;
    this.pttSafetyTimeout = null;
    this.pttTouchWatchdog = null;
    this._activeTouchCount = 0; // Track active touches on document
    this._isReceiving = false; // Track if someone else is speaking
    this._lastTxTime = {}; // Track last TX time per user session
    this._collapseTimer = null; // Timer for auto-collapse after speaking stops
    this.username = '';
    this.audioEngine = null;
    this.connected = false;
    this.gpsTracker = new GPSTracker();
    this.sessionManager = new PTTSessionManager();
    this._validatedSession = null; // Result from validate-session
    this._isListenOnly = false;    // Listen-only mode (can hear, can't transmit)
    this._autoJoinedChannel = false;

    // Auto-reconnect state
    this._intentionalDisconnect = false;
    this._reconnecting = false;
    this._reconnectAttempt = 0;
    this._reconnectTimer = null;
    this._reconnectMaxAttempts = 10;
    this._lastChannelId = null;
    this._lastChannelName = null;

    // GPS Map (tablet/desktop only)
    this.gpsMap = null;
    this._mapBreakpoint = 768;

    // GPS Map (mobile only, collapsible)
    this.gpsMapMobile = null;
    this._mobileMapCollapsed = localStorage.getItem('ptt_mobile_map_collapsed') !== 'false';

    // Detect mobile device
    this.isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    // Wake lock
    this._wakeLock = null;

    // Background state tracking
    this._appInBackground = false;

    // GPS health polling
    this._gpsHealthInterval = null;

    // Demo mode detection
    const hostname = window.location.hostname;
    const isLocalIP = /^(192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[01])\.|localhost|127\.0\.0\.1)/.test(hostname);
    this.demoMode = isLocalIP || window.location.search.includes('demo=1');

    // Track all touches on the document for stuck PTT detection
    document.addEventListener('touchstart', (e) => {
      this._activeTouchCount = e.touches.length;
    }, { passive: true });
    document.addEventListener('touchend', (e) => {
      this._activeTouchCount = e.touches.length;
    }, { passive: true });
    document.addEventListener('touchcancel', (e) => {
      this._activeTouchCount = e.touches.length;
    }, { passive: true });

    console.log('[INIT] Mobile device:', this.isMobile);
    console.log('[INIT] Using browser-native audio engine');
  }

  init() {
    // Get UI elements
    this.elements = {
      radioIndicator: document.getElementById('radio-indicator'),
      gpsIndicator: document.getElementById('gps-indicator'),
      connectPanel: document.getElementById('connect-panel'),
      mainPanel: document.getElementById('main-panel'),
      disconnectBtn: document.getElementById('disconnect-btn'),
      channelSelect: document.getElementById('channel-select'),
      userList: document.getElementById('user-list'),
      userListToggle: document.getElementById('user-list-toggle'),
      userListSummary: document.getElementById('user-list-summary'),
      userListChevron: document.getElementById('user-list-chevron'),
      micSelect: document.getElementById('mic-select'),
      speakerSelect: document.getElementById('speaker-select'),
      pttButton: document.getElementById('ptt-button'),
      gpsToggle: document.getElementById('gps-toggle'),
      gpsStatus: document.getElementById('gps-status'),
      // Auth elements
      authButtons: document.getElementById('auth-buttons'),
      oauthLoginBtn: document.getElementById('oauth-login-btn'),
      guestModeBtn: document.getElementById('guest-mode-btn'),
      guestForm: document.getElementById('guest-form'),
      guestTokenInput: document.getElementById('guest-token-input'),
      guestNameInput: document.getElementById('guest-name-input'),
      guestJoinBtn: document.getElementById('guest-join-btn'),
      guestBackBtn: document.getElementById('guest-back-btn'),
      authLoading: document.getElementById('auth-loading'),
      authLoadingText: document.getElementById('auth-loading-text'),
      authError: document.getElementById('auth-error'),
      authErrorText: document.getElementById('auth-error-text'),
      authRetryBtn: document.getElementById('auth-retry-btn')
    };

    // Setup event listeners
    this._setupEventListeners();

    // Register service worker with aggressive update checking
    this._registerServiceWorker();

    // Check for OAuth callback or existing session
    this._handleAuthOnLoad();

    // Create audio engine (used when connecting)
    if (!this.demoMode) {
      this.audioEngine = new SimpleMumbleAudio();
      console.log('[INIT] Browser-native audio engine ready');
    }

    // GPS map resize listener (tablet/desktop)
    this._onResize = () => this._handleMapResize();
    window.addEventListener('resize', this._onResize);

    console.log('PTT Integrated App initialized', this.demoMode ? '(Demo Mode)' : '(Live Mode)');
  }

  _showMobileRedirectMessage() {
    // Show a message explaining mobile users should use full mumble-web
    const message = document.createElement('div');
    message.style.cssText = 'position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); background: #2a2a3e; padding: 20px; border-radius: 10px; text-align: center; max-width: 90%; z-index: 9999;';
    message.innerHTML = `
      <h3 style="margin-top: 0; color: #fff;">Mobile PTT Available</h3>
      <p style="color: #ccc;">For the best mobile experience, use the full Mumble interface.</p>
      <button id="mobile-redirect-btn" style="background: #4CAF50; color: white; border: none; padding: 10px 20px; border-radius: 5px; font-size: 16px; cursor: pointer; margin: 10px;">
        Open Mumble Web
      </button>
      <button id="mobile-cancel-btn" style="background: #666; color: white; border: none; padding: 10px 20px; border-radius: 5px; font-size: 16px; cursor: pointer; margin: 10px;">
        Stay Here (Desktop UI)
      </button>
    `;
    document.body.appendChild(message);

    document.getElementById('mobile-redirect-btn').addEventListener('click', () => {
      window.location.href = '/?address=' + window.location.hostname + '&port=443/mumble-ws';
    });

    document.getElementById('mobile-cancel-btn').addEventListener('click', () => {
      message.remove();
      // Set to demo mode for testing UI
      this.demoMode = true;
      console.log('[MOBILE] User chose to stay - switching to demo mode');
    });
  }

  _setupMumbleFrame() {
    console.log('[SETUP] Creating mumble iframe...');

    // Create hidden iframe for mumble-web
    this.mumbleFrame = document.createElement('iframe');
    this.mumbleFrame.id = 'mumble-frame';

    // Build URL with connection parameters
    // Use the /mumble-ws WebSocket path configured in nginx
    const params = new URLSearchParams({
      address: window.location.hostname,
      port: '443/mumble-ws', // Use the WebSocket proxy path
      matrix: 'true' // Auto-connect mode
    });

    this.mumbleFrame.src = '/?' + params.toString();
    this.mumbleFrame.style.cssText = 'position: absolute; left: -9999px; width: 1px; height: 1px; opacity: 0; pointer-events: none;';
    this.mumbleFrame.allow = 'microphone; autoplay';

    // Wait for iframe to load
    this.mumbleFrame.onload = () => {
      console.log('[SETUP] Mumble iframe loaded with URL:', this.mumbleFrame.src);
      this.mumbleWindow = this.mumbleFrame.contentWindow;
      console.log('[SETUP] mumbleWindow assigned:', !!this.mumbleWindow);
    };

    this.mumbleFrame.onerror = (error) => {
      console.error('[SETUP] Mumble iframe error:', error);
    };

    console.log('[SETUP] Appending iframe to body...');
    document.body.appendChild(this.mumbleFrame);
    console.log('[SETUP] Iframe appended. ID:', this.mumbleFrame.id);
  }

  _setupEventListeners() {
    // Auth: OAuth login
    this.elements.oauthLoginBtn.addEventListener('click', () => {
      this.sessionManager.startOAuthLogin();
    });

    // Auth: Show guest form
    this.elements.guestModeBtn.addEventListener('click', () => {
      this.elements.authButtons.classList.add('hidden');
      this.elements.guestForm.classList.remove('hidden');
      // Focus token input if empty, otherwise name input
      if (this.elements.guestTokenInput && !this.elements.guestTokenInput.value) {
        this.elements.guestTokenInput.focus();
      } else {
        this.elements.guestNameInput.focus();
      }
    });

    // Auth: Guest join
    this.elements.guestJoinBtn.addEventListener('click', () => this._guestJoin());
    if (this.elements.guestTokenInput) {
      this.elements.guestTokenInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') this.elements.guestNameInput.focus();
      });
    }
    this.elements.guestNameInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') this._guestJoin();
    });

    // Auth: Guest back
    this.elements.guestBackBtn.addEventListener('click', () => {
      this.elements.guestForm.classList.add('hidden');
      this.elements.authButtons.classList.remove('hidden');
    });

    // Auth: Retry — if session is valid, retry Mumble connection; otherwise go to login
    this.elements.authRetryBtn.addEventListener('click', async () => {
      if (this._validatedSession && this.sessionManager.isAuthenticated()) {
        // Session still valid — retry just the radio connection
        // This tap IS a user gesture, so mic permission will work on iOS
        try {
          this._showAuthLoading('Connecting to radio...');
          this._setRadioStatus('connecting');
          this._connecting = true;
          // Clean up any previous partial connection
          if (this.audioEngine && this.audioEngine.connected) {
            try { await this.audioEngine.disconnect(); } catch(e) {}
          }
          // Get a fresh mumble token before retry
          try {
            const session = await this.sessionManager.validateSession();
            if (session && session.valid) this._validatedSession = session;
          } catch (e) { /* proceed with existing token */ }
          const connTimeout = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Connection timed out')), 15000)
          );
          const connectPromise = this.demoMode
            ? this._connectDemo(this.username)
            : this._connectMumble(this.username);
          connectPromise.catch(() => {});
          await Promise.race([connectPromise, connTimeout]);
        } catch (e) {
          this._connecting = false;
          console.error('[RETRY] Failed:', e);
          this._setRadioStatus('disconnected');
          if (this.audioEngine && this.audioEngine.connected) {
            try { await this.audioEngine.disconnect(); } catch(e2) {}
          }
          this._showAuthError('Connection failed: ' + e.message);
        }
      } else {
        // No valid session — go back to login
        this.elements.authError.classList.add('hidden');
        this.elements.authButtons.classList.remove('hidden');
      }
    });

    // Disconnect button
    this.elements.disconnectBtn.addEventListener('click', () => this._disconnect());

    // User list toggle
    if (this.elements.userListToggle) {
      this.elements.userListToggle.addEventListener('click', () => {
        this.elements.userList.classList.toggle('collapsed');
        this.elements.userListChevron.classList.toggle('open');
      });
    }

    // Channel selection
    if (this.elements.channelSelect) {
      this.elements.channelSelect.addEventListener('change', (e) => {
        const channelId = parseInt(e.target.value, 10);
        if (!isNaN(channelId) && this.audioEngine && this.connected) {
          this.audioEngine.switchChannel(channelId);
          this._lastChannelId = channelId;
          const ch = this.audioEngine.channels ? this.audioEngine.channels.get(channelId) : null;
          this._lastChannelName = ch ? ch.name : null;
          sessionStorage.setItem('ptt_last_channel', channelId);
          if (this._lastChannelName) sessionStorage.setItem('ptt_last_channel_name', this._lastChannelName);
        }
      });
    }

    // Speaker selection
    this.elements.speakerSelect.addEventListener('change', async (e) => {
      const deviceId = e.target.value;
      localStorage.setItem('ptt_speaker', deviceId);
      if (this.audioEngine && this.connected) {
        try {
          await this.audioEngine.setAudioOutput(deviceId);
          console.log('[APP] Audio output changed to:', deviceId || 'default');
        } catch (err) {
          console.error('[APP] Failed to change audio output:', err);
        }
      }
    });

    // Mic selection - save choice
    this.elements.micSelect.addEventListener('change', (e) => {
      localStorage.setItem('ptt_mic', e.target.value);
      console.log('[APP] Mic selection saved:', e.target.value);
    });

    // GPS toggle
    if (this.elements.gpsToggle) {
      // Restore saved preference
      this.elements.gpsToggle.checked = localStorage.getItem('ptt_gps_enabled') === 'true';

      this.elements.gpsToggle.addEventListener('change', (e) => {
        const enabled = e.target.checked;
        localStorage.setItem('ptt_gps_enabled', enabled);
        if (enabled && this.connected && this.username) {
          this.gpsTracker.start(this.username);
        } else {
          this.gpsTracker.stop();
        }
      });

      // Update status text when GPS status changes
      this.gpsTracker.onStatusChange = (status) => {
        this._updateGPSStatus(status);
      };
    }

    // Mobile map toggle
    const mobileMapHeader = document.getElementById('mobile-map-header');
    if (mobileMapHeader) {
      mobileMapHeader.addEventListener('click', () => this._toggleMobileMap());
    }

    // PTT Button - Touch events (on button)
    this.elements.pttButton.addEventListener('touchstart', (e) => this._onPTTStart(e), { passive: false });

    // PTT Button - Mouse events (on button)
    this.elements.pttButton.addEventListener('mousedown', (e) => this._onPTTStart(e));

    // Prevent context menu
    this.elements.pttButton.addEventListener('contextmenu', (e) => e.preventDefault());

    // CRITICAL: Listen for touch/mouse END on the DOCUMENT level
    // This catches releases even if the finger/mouse moves off the button
    document.addEventListener('touchend', (e) => {
      if (this.isPTTPressed) {
        console.log('[PTT] Document touchend - releasing PTT');
        this._onPTTEnd(e);
      }
    }, { passive: false });

    document.addEventListener('touchcancel', (e) => {
      if (this.isPTTPressed) {
        console.log('[PTT] Document touchcancel - releasing PTT');
        this._onPTTEnd(e);
      }
    }, { passive: false });

    document.addEventListener('mouseup', (e) => {
      if (this.isPTTPressed) {
        console.log('[PTT] Document mouseup - releasing PTT');
        this._onPTTEnd(e);
      }
    });

    // Handle visibility change - force release PTT if app goes to background
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this._appInBackground = true;
        if (this.isTransmitting || this.isPTTPressed) {
          console.log('[PTT] App hidden - force releasing PTT');
          this._forceReleasePTT();
        }
      } else {
        this._appInBackground = false;
        if (this.connected) {
          this._acquireWakeLock();
          // Flush stale audio buffered while suspended - don't replay old messages
          if (this.audioEngine) {
            this.audioEngine.flushAudioPlayers();
          }
          // Check if WebSocket died while in background
          if (this.audioEngine && this.audioEngine.ws &&
              this.audioEngine.ws.readyState !== WebSocket.OPEN) {
            console.warn('[VISIBILITY] WebSocket dead after resume');
            this._onUnexpectedDisconnect({ code: 4001, reason: 'Dead after resume', wasClean: false });
          }
        }
        // If already reconnecting and back online, retry immediately
        if (this._reconnecting && navigator.onLine) {
          if (this._reconnectTimer) { clearTimeout(this._reconnectTimer); this._reconnectTimer = null; }
          this._attemptReconnect();
        }
      }
    });

    // Touch point watchdog: if we're transmitting but no touches are on the screen, release
    document.addEventListener('touchstart', () => {
      // Any touch means user is interacting - good
    }, { passive: true });

    // Release mic when page is actually being destroyed.
    // Use both pagehide (with persisted check) and beforeunload for coverage.
    // pagehide with persisted=false means the page is being discarded (tab closed).
    // pagehide with persisted=true means bfcache (background) - don't disconnect.
    window.addEventListener('pagehide', (e) => {
      if (!e.persisted && this.connected) {
        console.log('[APP] Page being destroyed - disconnecting');
        this.gpsTracker.stop();
        this._disconnect();
      }
    });

    // Network: online/offline events
    window.addEventListener('online', () => {
      console.log('[NET] Online');
      if (this._reconnecting) {
        // Cancel pending timer and attempt immediately
        if (this._reconnectTimer) { clearTimeout(this._reconnectTimer); this._reconnectTimer = null; }
        this._attemptReconnect();
      }
    });

    window.addEventListener('offline', () => {
      console.log('[NET] Offline');
    });

    // Detect network type changes (Wi-Fi ↔ cellular) for fast reconnection
    if (navigator.connection) {
      navigator.connection.addEventListener('change', () => {
        console.log('[NET] Connection changed - type:', navigator.connection.type, 'effectiveType:', navigator.connection.effectiveType);
        if (this.connected && this.audioEngine && this.audioEngine.ws) {
          // Network interface changed - old TCP connection is dead, trigger immediate reconnect
          console.warn('[NET] Network switch detected - forcing reconnect');
          this.audioEngine.ws.close(4002, 'Network switch');
        }
      });
    }

    // beforeunload fires on tab close/navigation - reliable on desktop Safari
    window.addEventListener('beforeunload', () => {
      if (this.connected && this.audioEngine && this.audioEngine.micStream) {
        console.log('[APP] beforeunload - releasing mic');
        this.audioEngine.micStream.getTracks().forEach(t => t.stop());
      }
    });
  }

  async _acquireWakeLock() {
    if (!('wakeLock' in navigator)) return;
    try {
      this._wakeLock = await navigator.wakeLock.request('screen');
      this._wakeLock.addEventListener('release', () => { this._wakeLock = null; });
      console.log('[APP] Wake lock acquired');
    } catch (e) {
      console.warn('[APP] Wake lock failed:', e.message);
    }
  }

  _releaseWakeLock() {
    if (this._wakeLock) { this._wakeLock.release(); this._wakeLock = null; }
  }

  _requestNotificationPermission() {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(p => console.log('[APP] Notification permission:', p));
    }
  }

  _notifySpeaking(username) {
    if (!this._appInBackground) return;
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    const notif = new Notification('LiveFusion PTT', {
      body: `${username} is talking`,
      icon: 'icons/icon-192.png',
      tag: 'ptt-speaking',
      renotify: true
    });
    setTimeout(() => notif.close(), 5000);
    notif.onclick = () => { window.focus(); notif.close(); };
  }

  // --- Auth Handling ---

  async _handleAuthOnLoad() {
    const params = new URLSearchParams(window.location.search);

    // OAuth error callback: ?error=xxx&state=xxx — clean URL and show error
    if (params.has('error')) {
      window.history.replaceState({}, '', window.location.pathname);
      const desc = params.get('error_description') || params.get('error');
      if (params.get('error') !== 'access_denied') {
        this._showAuthError(desc);
      }
      // Fall through to check for existing session below
    }

    // OAuth callback: ?code=xxx&state=xxx
    if (params.has('code') && params.has('state')) {
      // Check if we have the OAuth state in sessionStorage.
      // If the browser was closed and reopened, sessionStorage is cleared,
      // making it impossible to complete the OAuth callback.
      const savedState = sessionStorage.getItem('ptt_oauth_state');
      if (!savedState) {
        console.warn('[AUTH] OAuth callback detected but no saved state (browser restarted?) — cleaning URL');
        window.history.replaceState({}, '', window.location.pathname);
        // Fall through to existing session check below
      } else {
        this._showAuthLoading('Completing login...');
        // Clean URL
        window.history.replaceState({}, '', window.location.pathname);
        try {
          await this.sessionManager.handleOAuthCallback(params.get('code'), params.get('state'));
          await this._authenticateAndConnect();
        } catch (e) {
          this._showAuthError(e.message);
        }
        return;
      }
    }

    // Guest invite link: ?guest=CODE
    if (params.has('guest')) {
      const guestCode = params.get('guest').trim().toUpperCase();
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
      // Pre-fill code and show guest form
      if (this.elements.guestTokenInput && guestCode) {
        this.elements.guestTokenInput.value = guestCode;
      }
      this.elements.authButtons.classList.add('hidden');
      this.elements.guestForm.classList.remove('hidden');
      this.elements.guestNameInput.focus();
      return;
    }

    // Existing session
    if (this.sessionManager.isAuthenticated()) {
      this._showAuthLoading('Reconnecting...');
      try {
        await this._authenticateAndConnect();
      } catch (e) {
        console.warn('[AUTH] Reconnect failed:', e.message);
        this.sessionManager.clearSession();
        this._showAuthButtons();
      }
    }
  }

  async _guestJoin() {
    const code = this.elements.guestTokenInput ? this.elements.guestTokenInput.value.trim().toUpperCase() : '';
    const name = this.elements.guestNameInput.value.trim();

    if (!code || code.length < 4) { this.elements.guestTokenInput.focus(); return; }
    if (!name || name.length < 2) { this.elements.guestNameInput.focus(); return; }
    if (!/^[A-Za-z0-9][A-Za-z0-9 _-]*$/.test(name)) {
      this.elements.guestNameInput.focus();
      this._showAuthError('Name can only contain letters, numbers, spaces, and hyphens.');
      return;
    }

    this.elements.guestJoinBtn.disabled = true;
    this._showAuthLoading('Joining team...');

    try {
      // Authenticate via invite code → creates guest session
      await this.sessionManager.joinAsGuest(code, name);
      // Validate session → gets team info, mumble username
      await this._authenticateAndConnect();
    } catch (error) {
      console.error('[CONNECT] Guest join error:', error);
      this.elements.guestJoinBtn.disabled = false;
      this._setRadioStatus('disconnected');
      this._showAuthError(error.message || 'Failed to join. Check your invite code.');
    }
  }

  async _authenticateAndConnect() {
    this._showAuthLoading('Validating session...');
    this._connecting = true;

    try {
      // Phase 1: Validate session (10s timeout)
      const valTimeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Validation timed out. Please try again.')), 10000)
      );
      const session = await Promise.race([this.sessionManager.validateSession(), valTimeout]);
      if (!session || !session.valid) {
        throw new Error('Session validation failed. Please log in again.');
      }

      this._validatedSession = session;
      this.username = session.username;
      this._isListenOnly = session.features && session.features.includes('radio-listen');
      console.log('[AUTH] Authenticated as:', this.username, session.is_guest ? '(guest)' : '(full account)', this._isListenOnly ? '(listen-only)' : '');

      // Phase 2: Connect to radio (15s timeout)
      this._showAuthLoading('Connecting to radio...');
      this._setRadioStatus('connecting');

      const connTimeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Radio connection timed out. Tap Try Again.')), 15000)
      );
      const connectPromise = this.demoMode
        ? this._connectDemo(this.username)
        : this._connectMumble(this.username);
      // Suppress unhandled rejection if timeout wins the race
      connectPromise.catch(() => {});
      await Promise.race([connectPromise, connTimeout]);

    } catch (error) {
      this._connecting = false;
      console.error('[CONNECT] Error:', error);
      this._setRadioStatus('disconnected');

      // Clean up any partial Mumble connection
      if (this.audioEngine && this.audioEngine.connected) {
        try { await this.audioEngine.disconnect(); } catch(e) {}
      }

      if (this._validatedSession && this.sessionManager.isAuthenticated()) {
        // Session valid but radio connection failed — offer retry
        this._showAuthError(error.message || 'Connection failed. Tap Try Again.');
      } else {
        this.sessionManager.clearSession();
        this._showAuthError(error.message || 'Authentication failed. Please log in again.');
      }
    }
  }

  _showAuthLoading(text) {
    this.elements.authButtons.classList.add('hidden');
    this.elements.guestForm.classList.add('hidden');
    this.elements.authError.classList.add('hidden');
    this.elements.authLoading.classList.remove('hidden');
    this.elements.authLoadingText.textContent = text || 'Authenticating...';
  }

  _showAuthError(msg) {
    this.elements.authButtons.classList.add('hidden');
    this.elements.guestForm.classList.add('hidden');
    this.elements.authLoading.classList.add('hidden');
    this.elements.authError.classList.remove('hidden');
    this.elements.authErrorText.textContent = msg;
  }

  _showAuthButtons() {
    this.elements.authLoading.classList.add('hidden');
    this.elements.authError.classList.add('hidden');
    this.elements.guestForm.classList.add('hidden');
    this.elements.authButtons.classList.remove('hidden');
  }

  _buildMumbleWsUrl() {
    if (!this._validatedSession || !this._validatedSession.mumble_token) return null;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const port = window.location.protocol === 'https:' ? '8443' : '8080';
    return `${protocol}//${window.location.hostname}:${port}/mumble-ws?token=${this._validatedSession.mumble_token}`;
  }

  async _connectMumble(username) {
    try {
      console.log('[CONNECT] Connecting with browser-native audio engine...');
      this._intentionalDisconnect = false;

      await this.audioEngine.connect({
        address: window.location.hostname,
        username: username,
        wsUrl: this._buildMumbleWsUrl(),
        onDisconnected: (event) => {
          this._onUnexpectedDisconnect(event);
        },
        onConnected: () => {
          // Guard: if connect was aborted (e.g. timeout), don't show UI
          if (!this._connecting) {
            console.warn('[CONNECT] onConnected fired after abort, cleaning up');
            if (this.audioEngine) this.audioEngine.disconnect().catch(() => {});
            return;
          }
          this._connecting = false;
          this._reconnecting = false;
          this._reconnectAttempt = 0;
          console.log('[CONNECT] Connection successful!');
          this.connected = true;
          this._setRadioStatus('connected');
          this._startGPSHealthPolling();
          this._showMainPanel();
          this._loadDevices();

          // Get the real user list from the audio engine (may already have users)
          if (this.audioEngine.users && this.audioEngine.users.size > 0) {
            this._updateUserList(Array.from(this.audioEngine.users.values()));
          } else {
            // Fallback if no users received yet
            this._updateUserList([
              { name: username, session: 0, isSelf: true }
            ]);
          }

          // Auth flow complete
          this._showRadioBadges(true);

          // Listen-only: disable PTT button and change label
          if (this._isListenOnly) {
            this.elements.pttButton.disabled = true;
            this.elements.pttButton.classList.add('listen-only');
            const label = this.elements.pttButton.querySelector('.ptt-label');
            if (label) label.textContent = 'LISTEN ONLY';
            const hint = this.elements.pttButton.querySelector('.ptt-hint');
            if (hint) hint.textContent = 'You can hear but cannot transmit';
          }

          this._acquireWakeLock();
          this._requestNotificationPermission();
          // Auto-start GPS if previously enabled
          if (localStorage.getItem('ptt_gps_enabled') === 'true') {
            this.gpsTracker.start(this.username);
          }
          // Start network type monitoring (reports WiFi/LTE to backend)
          this._startNetworkMonitoring();
          // Auto-join channel if ServerSync arrived during audio init (iOS timing race)
          if (!this._autoJoinedChannel && this.audioEngine.channels && this.audioEngine.channels.size > 0 && this.audioEngine.sessionId) {
            const sorted = Array.from(this.audioEngine.channels.values())
              .sort((a, b) => (a.position - b.position) || (a.name || '').localeCompare(b.name || ''));
            this._autoJoinChannel(sorted);
          }
          // Init GPS map on tablet/desktop
          this._initGPSMap();
          // Init GPS map on mobile (separate instance)
          this._initMobileMap();

          // Refresh device dropdowns when Bluetooth/headset connects or disconnects
          this.audioEngine.onDeviceChange = () => {
            console.log('[APP] Audio device changed — refreshing device list');
            this._loadDevices();
          };

        },
        onError: (error) => {
          console.error('[CONNECT] Audio engine error:', error);
        },
        onUsersChanged: (users) => {
          console.log('[CONNECT] Users changed:', users);
          this._updateUserList(users);
        },
        onChannelsChanged: (channels) => {
          this._updateChannelList(channels);
        },
        onQualityChanged: (quality, rtt, jitter) => {
          this._setConnectionQuality(quality, rtt, jitter);
        },
        onServerSync: () => {
          // Session ID is now set — safe to switch channels
          console.log('[CONNECT] ServerSync received, sessionId:', this.audioEngine.sessionId);
          if (this.audioEngine.channels && this.audioEngine.channels.size > 0) {
            const sorted = Array.from(this.audioEngine.channels.values())
              .sort((a, b) => (a.position - b.position) || (a.name || '').localeCompare(b.name || ''));
            this._autoJoinChannel(sorted);
          }
        }
      });

    } catch (error) {
      console.error('[CONNECT] Connection failed:', error);
      throw error;
    }
  }


  async _connectDemo(username) {
    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      console.log('Microphone access granted');

      // Simulate connection
      setTimeout(() => {
        this._setRadioStatus('connected');
        this._startGPSHealthPolling();
        this._showMainPanel();
        this._loadDevices();
        if (this.elements.channelSelect) {
          this.elements.channelSelect.innerHTML = '<option value="0">Operations (Demo)</option>';
        }
        this._updateUserList([
          { name: username, session: 1, isSelf: true },
          { name: 'Demo User 1', session: 2, isSelf: false },
          { name: 'Demo User 2', session: 3, isSelf: false }
        ]);
        this.connected = true;
      }, 500);

    } catch (error) {
      console.error('Connection failed:', error);
      this._setRadioStatus('disconnected');
    } finally {
      // connectBtn removed — auth flow handles state
    }
  }

  async _connectReal(username) {
    try {
      console.log('[CONNECT] Starting _connectReal, username:', username);
      console.log('[CONNECT] mumbleFrame exists:', !!this.mumbleFrame);
      console.log('[CONNECT] mumbleWindow exists:', !!this.mumbleWindow);

      if (!this.mumbleFrame) {
        console.error('[CONNECT] No mumble frame! This should not happen.');
        throw new Error('Mumble frame not initialized');
      }

      console.log('[CONNECT] Waiting for mumble-web to initialize...');

      // Wait for mumble-web UI to be available
      let attempts = 0;
      const maxAttempts = 50;

      this.checkInterval = setInterval(() => {
        attempts++;
        console.log(`[CONNECT] Attempt ${attempts}/${maxAttempts} - checking for mumbleUi`);

        // Try to access mumble-web's UI
        if (this.mumbleWindow && this.mumbleWindow.mumbleUi) {
          console.log('[CONNECT] Found mumbleUi! Proceeding to connect.');
          clearInterval(this.checkInterval);
          this._doMumbleConnect(username);
        } else {
          if (attempts % 5 === 0) {
            console.log(`[CONNECT] Still waiting... mumbleWindow:`, !!this.mumbleWindow, 'mumbleUi:', this.mumbleWindow ? !!this.mumbleWindow.mumbleUi : 'N/A');
          }
          if (attempts >= maxAttempts) {
            clearInterval(this.checkInterval);
            console.error('[CONNECT] Timeout - mumble-web failed to load');
            throw new Error('Mumble-web failed to load after ' + (maxAttempts * 200) + 'ms');
          }
        }
      }, 200);

    } catch (error) {
      console.error('[CONNECT] Connection failed:', error);
      this._setRadioStatus('disconnected');
      // connectBtn removed — auth flow handles state
    }
  }

  _doMumbleConnect(username) {
    try {
      const ui = this.mumbleWindow.mumbleUi;
      console.log('Mumble UI found, connecting...', ui);

      // Check if mumble-web has any errors
      if (this.mumbleWindow.console && this.mumbleWindow.console.error) {
        const originalError = this.mumbleWindow.console.error;
        this.mumbleWindow.console.error = (...args) => {
          console.error('[MUMBLE-WEB ERROR]', ...args);
          originalError.apply(this.mumbleWindow.console, args);
        };
      }

      // Set connection parameters
      if (ui.connectDialog) {
        console.log('Setting connection parameters...');

        // Set username
        if (ui.connectDialog.username) {
          ui.connectDialog.username(username);
          console.log('[CONNECT] Username set in mumble-web');
        }

        // Set server address (use default from mumble-web config)
        // mumble-web should have server configured in config.js

        // Trigger connect
        if (ui.connectDialog.connect) {
          console.log('Triggering connection...');
          try {
            ui.connectDialog.connect();
            console.log('[CONNECT] connect() called successfully');
          } catch (err) {
            console.error('[CONNECT] Error calling connect():', err);
          }
        }
      } else {
        console.log('No connect dialog, checking if already connected...');
      }

      // Monitor for connection
      let attempts = 0;
      const monitor = setInterval(() => {
        attempts++;

        if (ui.client && ui.client.self) {
          clearInterval(monitor);
          this._onMumbleConnected(ui);
        } else if (attempts > 75) { // 15 seconds at 200ms intervals
          clearInterval(monitor);
          if (!this.connected) {
            console.error('Connection timeout after', attempts, 'attempts');
            this._setRadioStatus('disconnected');
            // Auth flow complete
          }
        } else if (attempts % 10 === 0) {
          console.log('Waiting for connection...', attempts, 'attempts');
        }
      }, 200);

    } catch (error) {
      console.error('Mumble connect error:', error);
      this._setRadioStatus('disconnected');
      // connectBtn removed — auth flow handles state
    }
  }

  _onMumbleConnected(ui) {
    console.log('Connected to Mumble!');
    this.connected = true;
    this._setRadioStatus('connected');
    this._startGPSHealthPolling();
    this._showMainPanel();
    this._loadDevices();

    // Debug: Search for the actual voice handler (PushToTalkVoiceHandler)
    console.log('Searching for voice handler...');
    console.log('ui keys:', Object.keys(ui));
    console.log('ui._micNode:', ui._micNode);

    // Check common locations
    const checkLocations = [
      'ui._voiceHandler',
      'ui.voiceHandler',
      'ui._voice',
      'ui.voice',
      'ui._micStream',
      'ui.micStream'
    ];

    checkLocations.forEach(path => {
      const parts = path.split('.');
      let obj = ui;
      for (let i = 1; i < parts.length; i++) {
        obj = obj?.[parts[i]];
      }
      if (obj) {
        console.log(`${path}:`, obj);
        console.log(`${path} type:`, obj.constructor?.name);
        if (obj._pushed !== undefined) {
          console.log(`FOUND IT! Voice handler at ${path}`);
        }
      }
    });

    // Check if _micNode has a destination
    if (ui._micNode && ui._micNode.numberOfOutputs > 0) {
      console.log('_micNode has outputs, checking connections...');
    }

    console.log('Current voice mode (settings):', ui.settings.voiceMode);

    // Update UI with real data
    this._updateChannelFromMumble(ui);
    this._updateUsersFromMumble(ui);

    // Listen for changes
    if (ui.client) {
      ui.client.on('newUser', () => this._updateUsersFromMumble(ui));
      ui.client.on('removeUser', () => this._updateUsersFromMumble(ui));
      ui.client.on('newChannel', () => this._updateChannelFromMumble(ui));
      ui.client.on('error', (err) => {
        console.error('Mumble error:', err);
        this._disconnect();
      });
    }

    // Auth flow handles button state
  }

  _updateChannelFromMumble(ui) {
    if (ui.client && ui.client.self && ui.client.self.channel) {
      if (this.elements.channelSelect) {
        this.elements.channelSelect.innerHTML = '';
        const option = document.createElement('option');
        option.value = '';
        option.textContent = ui.client.self.channel.name;
        this.elements.channelSelect.appendChild(option);
      }
    }
  }

  _updateUsersFromMumble(ui) {
    if (!ui.client) return;

    const users = (ui.client.users || []).map(u => ({
      name: u.name,
      session: u.session,
      isSelf: u === ui.client.self,
      talking: u.talking
    }));

    this._updateUserList(users);
  }

  _onPTTStart(e) {
    if (e) e.preventDefault();

    if (this._isListenOnly) return;
    if (this.elements.pttButton.disabled || this.isPTTPressed) return;

    console.log('[PTT] Start');
    this.isPTTPressed = true;
    this._haptic('press');
    this._startTransmitting();

    // Safety: auto-release after 60 seconds
    this.pttSafetyTimeout = setTimeout(() => {
      console.warn('[PTT] Safety timeout - auto-releasing');
      this._forceReleasePTT();
    }, 60000);

    // Touch watchdog: check every 500ms if there are still active touches
    // This catches the case where touchend never fires
    if (this.isMobile) {
      this.pttTouchWatchdog = setInterval(() => {
        // Check if there are any active touch points on the document
        // If transmitting but no touches on screen, release immediately
        if (this.isPTTPressed && !this._hasActiveTouches()) {
          console.warn('[PTT] No active touches detected - releasing stuck PTT');
          this._forceReleasePTT();
        }
      }, 500);
    }
  }

  _onPTTEnd(e) {
    if (!this.isPTTPressed) return;
    console.log('[PTT] End');
    this._haptic('release');

    if (this.pttSafetyTimeout) { clearTimeout(this.pttSafetyTimeout); this.pttSafetyTimeout = null; }
    if (this.pttTouchWatchdog) { clearInterval(this.pttTouchWatchdog); this.pttTouchWatchdog = null; }

    this.isPTTPressed = false;
    if (this.isTransmitting) { this._stopTransmitting(); }
    if (this.elements.pttButton) { this.elements.pttButton.classList.remove('transmitting'); }
  }

  /**
   * Check if there are any active touch points on the screen
   */
  _hasActiveTouches() {
    // navigator.maxTouchPoints tells us if touch is supported
    // But we need to check actual current touches
    // We track this via a document-level listener
    return this._activeTouchCount > 0;
  }

  /**
   * Force release PTT - clears all state and stops transmission
   */
  _forceReleasePTT() {
    // Clear timers
    if (this.pttSafetyTimeout) {
      clearTimeout(this.pttSafetyTimeout);
      this.pttSafetyTimeout = null;
    }
    if (this.pttTouchWatchdog) {
      clearInterval(this.pttTouchWatchdog);
      this.pttTouchWatchdog = null;
    }

    // Reset state
    this.isPTTPressed = false;

    // Stop transmission immediately (no drain)
    if (this.isTransmitting) {
      this.isTransmitting = false;
      this.elements.pttButton.classList.remove('transmitting');
      this._setTX(false);
      if (!this.demoMode && this.audioEngine) {
        try {
          this.audioEngine.stopTransmission(true);  // immediate=true, no drain
        } catch (e) {
          console.error('[PTT] Error stopping transmission:', e);
        }
      }
    }

    // Visual cleanup
    if (this.elements.pttButton) {
      this.elements.pttButton.classList.remove('transmitting');
    }
  }

  _startTransmitting() {
    if (this.isTransmitting) return;
    this.isTransmitting = true;
    this.elements.pttButton.classList.add('transmitting');
    this._setTX(true);
    if (!this.demoMode && this.audioEngine) {
      this.audioEngine.startTransmission();  // Engine handles drain-resume
    }
  }

  _stopTransmitting() {
    if (!this.isTransmitting) return;
    this.isTransmitting = false;
    this.elements.pttButton.classList.remove('transmitting');
    this._setTX(false);

    if (!this.demoMode && this.audioEngine) {
      try {
        this.audioEngine.stopTransmission();  // Engine drains internally
      } catch (e) {
        console.error('[PTT] Error stopping transmission:', e);
      }
    }
  }

  /**
   * Set the TX badge active/inactive
   */
  _setTX(active) {
    const badge = document.getElementById('tx-badge');
    if (badge) badge.className = active ? 'radio-badge tx-active' : 'radio-badge';
  }

  /**
   * Set the RX badge active/inactive
   */
  _setRX(active) {
    const badge = document.getElementById('rx-badge');
    if (badge) badge.className = active ? 'radio-badge rx-active' : 'radio-badge';
  }

  /**
   * Show/hide the radio badges container
   */
  _showRadioBadges(visible) {
    const container = document.getElementById('radio-badges');
    if (container) container.className = visible ? 'radio-badges visible' : 'radio-badges';
  }

  _updateGPSStatus(status) {
    const el = this.elements.gpsStatus;
    if (!el) return;
    el.className = 'gps-status';
    switch (status) {
      case 'sending':
        el.textContent = 'Sending';
        el.classList.add('sending');
        break;
      case 'waiting':
        el.textContent = 'Waiting for fix...';
        break;
      case 'no-permission':
        el.textContent = 'No Permission';
        el.classList.add('no-permission');
        break;
      case 'unavailable':
        el.textContent = 'Unavailable';
        break;
      default:
        el.textContent = 'Off';
    }
  }

  _updateChannelList(channels) {
    const select = this.elements.channelSelect;
    if (!select) return;

    const isGuest = !this._validatedSession || this._validatedSession.is_guest;

    let currentChannelId = 0;
    if (this.audioEngine && this.audioEngine.users) {
      const self = Array.from(this.audioEngine.users.values()).find(u => u.isSelf);
      if (self && self.channelId !== undefined) currentChannelId = self.channelId;
    }

    let visibleChannels;
    if (isGuest && this._validatedSession && (this._validatedSession.team_name || this._validatedSession.team_tag)) {
      // Token guest: lock to their team channel
      const teamName = this._validatedSession.team_name;
      const teamTag = this._validatedSession.team_tag;
      visibleChannels = channels.filter(ch => {
        if (!ch.name || ch.channelId === 0) return false;
        const chLower = ch.name.toLowerCase();
        if (teamName && chLower === teamName.toLowerCase()) return true;
        if (teamTag && chLower === teamTag.toLowerCase()) return true;
        if (teamName && chLower.includes(teamName.toLowerCase())) return true;
        if (teamTag && chLower.includes(teamTag.toLowerCase())) return true;
        return false;
      });
      // Fall back to Guest channel if team channel not found
      if (visibleChannels.length === 0) {
        visibleChannels = channels.filter(ch => ch.name && ch.name.toLowerCase() === 'guest');
      }
    } else if (isGuest) {
      // Guest without team info: lock to Guest channel
      visibleChannels = channels.filter(ch => ch.name && ch.name.toLowerCase() === 'guest');
    } else {
      // Full user: all channels
      visibleChannels = channels;
    }

    select.innerHTML = '';
    visibleChannels.forEach(ch => {
      const option = document.createElement('option');
      option.value = ch.channelId;
      option.textContent = ch.name || `Channel ${ch.channelId}`;
      if (ch.channelId === currentChannelId) option.selected = true;
      select.appendChild(option);
    });

    select.disabled = isGuest;
  }

  _autoJoinChannel(channels) {
    if (this._autoJoinedChannel) return;
    if (!channels || channels.length === 0) return;
    if (!this.audioEngine || !this.audioEngine.sessionId) return;

    let targetChannel = null;

    if (this._validatedSession) {
      const teamName = this._validatedSession.team_name;
      const teamTag = this._validatedSession.team_tag;

      targetChannel = channels.find(ch => {
        if (!ch.name || ch.channelId === 0) return false;
        const chLower = ch.name.toLowerCase();
        if (teamName && chLower === teamName.toLowerCase()) return true;
        if (teamTag && chLower === teamTag.toLowerCase()) return true;
        if (teamName && chLower.includes(teamName.toLowerCase())) return true;
        if (teamTag && chLower.includes(teamTag.toLowerCase())) return true;
        return false;
      });
    }

    if (!targetChannel) {
      targetChannel = channels.find(ch => ch.name && ch.name.toLowerCase() === 'guest');
    }

    if (targetChannel && targetChannel.channelId !== 0) {
      console.log('[CHANNEL] Auto-joined:', targetChannel.name);
      this.audioEngine.switchChannel(targetChannel.channelId);
      this._autoJoinedChannel = true;
      this._lastChannelId = targetChannel.channelId;
      this._lastChannelName = targetChannel.name;
      sessionStorage.setItem('ptt_last_channel', targetChannel.channelId);
      sessionStorage.setItem('ptt_last_channel_name', targetChannel.name);

      if (this.elements.channelSelect) {
        this.elements.channelSelect.value = targetChannel.channelId;
      }
    } else {
      console.log('[AUTOJOIN] No target channel found!');
    }
  }

  _handleMapResize() {
    const wide = window.innerWidth >= this._mapBreakpoint;
    if (wide && !this.gpsMap && this.connected) {
      this._initGPSMap();
    } else if (!wide && this.gpsMap) {
      this._destroyGPSMap();
    }
  }

  _initGPSMap() {
    if (this.gpsMap || window.innerWidth < this._mapBreakpoint) return;
    const getToken = () => this.sessionManager.getToken();
    const teamId = this._validatedSession?.team_id ?? null;
    this.gpsMap = new GPSMap('map-container', this.username, { getToken, teamId });
    this.gpsMap.init();
    console.log('[APP] GPS map initialized');
  }

  _destroyGPSMap() {
    if (this.gpsMap) {
      this.gpsMap.destroy();
      this.gpsMap = null;
      console.log('[APP] GPS map destroyed');
    }
  }

  _initMobileMap() {
    if (this.gpsMapMobile || !this.isMobile) return;
    const getToken = () => this.sessionManager.getToken();
    const teamId = this._validatedSession?.team_id ?? null;
    this.gpsMapMobile = new GPSMap('mobile-map-container', this.username, { getToken, teamId });
    this.gpsMapMobile.init();
    // Apply saved collapsed state
    const body = document.getElementById('mobile-map-body');
    const chevron = document.getElementById('mobile-map-chevron');
    const mainPanel = document.getElementById('main-panel');
    if (this._mobileMapCollapsed) {
      body?.classList.add('collapsed');
      chevron?.classList.remove('open');
      mainPanel?.classList.remove('mobile-map-open');
    } else {
      body?.classList.remove('collapsed');
      chevron?.classList.add('open');
      mainPanel?.classList.add('mobile-map-open');
      setTimeout(() => this.gpsMapMobile?.map?.invalidateSize(), 350);
    }
    console.log('[APP] Mobile GPS map initialized');
  }

  _destroyMobileMap() {
    if (this.gpsMapMobile) {
      this.gpsMapMobile.destroy();
      this.gpsMapMobile = null;
      console.log('[APP] Mobile GPS map destroyed');
    }
  }

  _toggleMobileMap() {
    const body = document.getElementById('mobile-map-body');
    const chevron = document.getElementById('mobile-map-chevron');
    if (!body) return;

    this._mobileMapCollapsed = !this._mobileMapCollapsed;
    localStorage.setItem('ptt_mobile_map_collapsed', this._mobileMapCollapsed);

    const mainPanel = document.getElementById('main-panel');
    if (this._mobileMapCollapsed) {
      body.classList.add('collapsed');
      chevron?.classList.remove('open');
      mainPanel?.classList.remove('mobile-map-open');
    } else {
      body.classList.remove('collapsed');
      chevron?.classList.add('open');
      mainPanel?.classList.add('mobile-map-open');
      // Leaflet needs invalidateSize after container becomes visible
      setTimeout(() => this.gpsMapMobile?.map?.invalidateSize(), 350);
    }
  }

  _disconnect() {
    this._intentionalDisconnect = true;
    this._cancelReconnect();

    if (!this.demoMode && this.audioEngine) {
      this.audioEngine.disconnect();
    }

    this.gpsTracker.stop();
    this._destroyGPSMap();
    this._destroyMobileMap();
    this._stopGPSHealthPolling();
    this._releaseWakeLock();
    if (this._networkCleanup) { this._networkCleanup(); this._networkCleanup = null; }
    this.connected = false;
    this._validatedSession = null;
    this._autoJoinedChannel = false;
    this._lastChannelId = null;
    this._lastChannelName = null;
    this.sessionManager.clearSession();
    this.elements.guestJoinBtn.disabled = false;
    if (this.elements.guestTokenInput) this.elements.guestTokenInput.value = '';
    this._setRadioStatus('disconnected');
    this._showRadioBadges(false);
    this._showConnectPanel();
    this._showAuthButtons();
  }

  // --- Auto-Reconnect ---

  _onUnexpectedDisconnect(event) {
    if (this._intentionalDisconnect || this._reconnecting) return;

    console.warn('[RECONNECT] Unexpected disconnect - code:', event.code, 'reason:', event.reason, 'kicked:', event.kickedByServer);
    this.connected = false;
    this._autoJoinedChannel = false;

    // Force-release PTT if transmitting
    if (this.isTransmitting || this.isPTTPressed) {
      this._forceReleasePTT();
    }

    // If the server kicked us (another device connected with our username), don't reconnect
    if (event.kickedByServer) {
      console.warn('[RECONNECT] Kicked by server - another device is using this username');
      this._setRadioStatus('disconnected');
      this._showReconnectFailedUI('Disconnected: another device logged in with your username.');
      return;
    }

    // Save current channel ID so we can rejoin after reconnect.
    // Prefer the already-known _lastChannelId (set by switchChannel / auto-join)
    // over user state, which may still show Root if a channel-switch confirmation
    // hadn't arrived yet from a previous reconnect.
    if (this._lastChannelId == null) {
      if (this.audioEngine && this.audioEngine.users) {
        const self = Array.from(this.audioEngine.users.values()).find(u => u.isSelf);
        if (self && self.channelId !== undefined && self.channelId !== 0) {
          this._lastChannelId = self.channelId;
          sessionStorage.setItem('ptt_last_channel', self.channelId);
        }
      }
    }
    // Fall back to sessionStorage
    if (this._lastChannelId == null) {
      const saved = sessionStorage.getItem('ptt_last_channel');
      if (saved !== null) this._lastChannelId = parseInt(saved, 10);
    }
    // Also save channel name (name matching survives server restarts)
    if (this._lastChannelId != null && !this._lastChannelName && this.audioEngine && this.audioEngine.channels) {
      const ch = this.audioEngine.channels.get(this._lastChannelId);
      if (ch) {
        this._lastChannelName = ch.name;
        sessionStorage.setItem('ptt_last_channel_name', ch.name);
      }
    }
    if (!this._lastChannelName) {
      this._lastChannelName = sessionStorage.getItem('ptt_last_channel_name');
    }
    console.log('[RECONNECT] Saved last channel:', this._lastChannelId, this._lastChannelName);

    this._startReconnect();
  }

  _startReconnect() {
    // Detect reconnect loops: if we've started reconnecting 3+ times in 30s,
    // another client is likely using the same username and kicking us off.
    const now = Date.now();
    if (!this._reconnectTimestamps) this._reconnectTimestamps = [];
    this._reconnectTimestamps.push(now);
    // Keep only timestamps from the last 30 seconds
    this._reconnectTimestamps = this._reconnectTimestamps.filter(t => now - t < 30000);
    if (this._reconnectTimestamps.length >= 3) {
      console.error('[RECONNECT] Reconnect loop detected - another device may be using this username');
      this._reconnecting = false;
      this._reconnectTimestamps = [];
      this._setRadioStatus('disconnected');
      this._showReconnectFailedUI('Another device may be connected with your username. Please disconnect the other session first.');
      return;
    }

    this._reconnecting = true;
    this._reconnectAttempt = 0;
    this._setRadioStatus('connecting');
    this._showReconnectingUI();
    this._scheduleReconnectAttempt();
  }

  _scheduleReconnectAttempt() {
    if (this._intentionalDisconnect || !this._reconnecting) return;

    if (this._reconnectAttempt >= this._reconnectMaxAttempts) {
      console.error('[RECONNECT] Max attempts reached - giving up');
      this._reconnecting = false;
      this._setRadioStatus('disconnected');
      this._showReconnectFailedUI();
      return;
    }

    // If offline, wait for online event instead of burning attempts
    if (!navigator.onLine) {
      console.log('[RECONNECT] Offline - waiting for network');
      return;
    }

    // Server handles ghost sessions instantly ("Disconnecting ghost"), so no need to wait.
    // First attempt: 2s (let browser finish network handoff), then exponential backoff capped at 15s
    const delay = Math.max(2000, Math.min(1000 * Math.pow(2, this._reconnectAttempt), 15000));
    console.log(`[RECONNECT] Attempt ${this._reconnectAttempt + 1}/${this._reconnectMaxAttempts} in ${delay}ms`);

    this._reconnectTimer = setTimeout(() => this._attemptReconnect(), delay);
  }

  async _attemptReconnect() {
    if (this._intentionalDisconnect || !this._reconnecting) return;

    this._reconnectAttempt++;
    console.log(`[RECONNECT] Attempting reconnect #${this._reconnectAttempt}`);

    try {
      // Get a fresh mumble token before reconnecting
      try {
        const session = await this.sessionManager.validateSession();
        if (session && session.valid) {
          this._validatedSession = session;
        }
      } catch (e) {
        console.warn('[RECONNECT] Failed to refresh mumble token:', e.message);
      }

      await this.audioEngine.reconnect({
        address: window.location.hostname,
        username: this.username,
        wsUrl: this._buildMumbleWsUrl(),
        onDisconnected: (event) => {
          this._onUnexpectedDisconnect(event);
        },
        onUsersChanged: (users) => {
          this._updateUserList(users);
        },
        onChannelsChanged: (channels) => {
          this._updateChannelList(channels);
        },
        onQualityChanged: (quality, rtt, jitter) => {
          this._setConnectionQuality(quality, rtt, jitter);
        },
        onServerSync: () => {
          if (!this.audioEngine) return;
          const channels = this.audioEngine.channels;

          let targetId = null;

          // Try saved channel ID first (works if server hasn't restarted)
          if (this._lastChannelId != null && channels.has(this._lastChannelId)) {
            targetId = this._lastChannelId;
            console.log('[RECONNECT] Found saved channel by ID:', targetId);
          }

          // Fall back to channel name matching (survives server restarts)
          if (targetId == null && this._lastChannelName) {
            for (const [id, ch] of channels) {
              if (ch.name === this._lastChannelName && id !== 0) {
                targetId = id;
                console.log('[RECONNECT] Found saved channel by name:', this._lastChannelName, '→ ID:', targetId);
                break;
              }
            }
          }

          // Last resort: team-based auto-join
          if (targetId == null) {
            console.log('[RECONNECT] Saved channel not found, falling back to auto-join');
            if (channels.size > 0) {
              const sorted = Array.from(channels.values())
                .sort((a, b) => (a.position - b.position) || (a.name || '').localeCompare(b.name || ''));
              this._autoJoinChannel(sorted);
            }
            return;
          }

          console.log('[RECONNECT] Rejoining channel:', targetId);
          this.audioEngine.switchChannel(targetId);
          this._lastChannelId = targetId;
          this._autoJoinedChannel = true;
          sessionStorage.setItem('ptt_last_channel', targetId);

          // Sync dropdown immediately
          if (this.elements.channelSelect) {
            this.elements.channelSelect.value = targetId;
          }

          // Verify channel switch after delay and retry if still on Root
          setTimeout(() => {
            if (!this.connected || !this.audioEngine) return;
            const self = Array.from(this.audioEngine.users.values()).find(u => u.isSelf);
            const actualChannel = self ? self.channelId : null;

            if (actualChannel === 0 && targetId !== 0) {
              // Still in Root — try switching again
              console.warn('[RECONNECT] Still in Root after switch, retrying...');
              this.audioEngine.switchChannel(targetId);
            }

            // Update dropdown to reflect actual state
            if (this.audioEngine.channels && this.audioEngine.channels.size > 0) {
              const sorted = Array.from(this.audioEngine.channels.values())
                .sort((a, b) => (a.position - b.position) || (a.name || '').localeCompare(b.name || ''));
              this._updateChannelList(sorted);
            }
          }, 2000);
        }
      });

      // Success
      console.log('[RECONNECT] Reconnected successfully');
      this.connected = true;
      this._reconnecting = false;
      this._reconnectAttempt = 0;
      this._setRadioStatus('connected');
      this._hideReconnectingUI();

    } catch (err) {
      console.warn('[RECONNECT] Attempt failed:', err.message);
      this._scheduleReconnectAttempt();
    }
  }

  _cancelReconnect() {
    this._reconnecting = false;
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
    this._hideReconnectingUI();
  }

  // --- Reconnect UI ---

  _showReconnectingUI() {
    this.elements.pttButton.disabled = true;
    // Remove existing banner if any
    const existing = document.getElementById('reconnect-banner');
    if (existing) existing.remove();

    const banner = document.createElement('div');
    banner.id = 'reconnect-banner';
    banner.innerHTML = '<span class="reconnect-spinner"></span><span>Reconnecting...</span>';

    const mainPanel = document.getElementById('main-panel');
    if (mainPanel) mainPanel.insertBefore(banner, mainPanel.firstChild);
  }

  _hideReconnectingUI() {
    const banner = document.getElementById('reconnect-banner');
    if (banner) banner.remove();
    if (this.connected) {
      this.elements.pttButton.disabled = false;
    }
  }

  _showReconnectFailedUI(message) {
    const existing = document.getElementById('reconnect-banner');
    if (existing) existing.remove();

    const banner = document.createElement('div');
    banner.id = 'reconnect-banner';
    banner.className = 'failed';
    banner.innerHTML = `
      <span>${message || 'Connection lost'}</span>
      <button id="reconnect-retry-btn">Retry</button>
      <button id="reconnect-disconnect-btn">Disconnect</button>
    `;

    const mainPanel = document.getElementById('main-panel');
    if (mainPanel) mainPanel.insertBefore(banner, mainPanel.firstChild);

    document.getElementById('reconnect-retry-btn').addEventListener('click', () => {
      this._hideReconnectingUI();
      this._startReconnect();
    });
    document.getElementById('reconnect-disconnect-btn').addEventListener('click', () => {
      this._disconnect();
    });
  }

  async _loadDevices() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const savedMic = localStorage.getItem('ptt_mic');
      const savedSpeaker = localStorage.getItem('ptt_speaker');

      // Populate microphone select
      this.elements.micSelect.innerHTML = '';
      devices.filter(d => d.kind === 'audioinput').forEach(device => {
        const option = document.createElement('option');
        option.value = device.deviceId;
        option.textContent = device.label || `Microphone ${device.deviceId.slice(0, 8)}`;
        if (savedMic && device.deviceId === savedMic) option.selected = true;
        this.elements.micSelect.appendChild(option);
      });

      // Populate speaker select
      this.elements.speakerSelect.innerHTML = '';
      const outputDevices = devices.filter(d => d.kind === 'audiooutput');

      if (outputDevices.length === 0) {
        // iOS/Mobile: audiooutput not available
        const option = document.createElement('option');
        option.value = '';
        option.textContent = 'System Default (iOS)';
        this.elements.speakerSelect.appendChild(option);
        this.elements.speakerSelect.disabled = true;
      } else {
        outputDevices.forEach(device => {
          const option = document.createElement('option');
          option.value = device.deviceId;
          option.textContent = device.label || `Speaker ${device.deviceId.slice(0, 8)}`;
          if (savedSpeaker && device.deviceId === savedSpeaker) option.selected = true;
          this.elements.speakerSelect.appendChild(option);
        });

        // Apply saved speaker to audio engine
        if (savedSpeaker && this.audioEngine && this.connected) {
          try {
            await this.audioEngine.setAudioOutput(savedSpeaker);
          } catch (err) {
            console.warn('[APP] Could not restore saved speaker:', err);
          }
        }
      }

      // Enable PTT button
      this.elements.pttButton.disabled = false;
    } catch (err) {
      console.error('Failed to enumerate devices:', err);
    }
  }

  _updateUserList(users) {
    this.elements.userList.innerHTML = '';

    // Filter to only users in the same channel as self
    const self = users.find(u => u.isSelf);
    const myChannelId = self ? self.channelId : null;
    let channelUsers = myChannelId != null
      ? users.filter(u => u.channelId === myChannelId)
      : users;

    // Deduplicate by username — keep only the newest session (highest session ID)
    // to filter out ghost sessions left by dead proxy connections
    const byName = new Map();
    channelUsers.forEach(u => {
      const existing = byName.get(u.name);
      if (!existing || u.session > existing.session) {
        byName.set(u.name, u);
      }
    });
    channelUsers = Array.from(byName.values());

    // Update GPS map filter with current channel member names
    const channelNames = channelUsers.map(u => u.name).filter(Boolean);
    if (this.gpsMap) this.gpsMap.setChannelMembers(channelNames);
    if (this.gpsMapMobile) this.gpsMapMobile.setChannelMembers(channelNames);

    // Check if anyone else is speaking (for RX indicator)
    const someoneElseSpeaking = channelUsers.some(u => u.speaking && !u.isSelf);
    // Haptic + notification on RX start (transition from not receiving to receiving)
    if (someoneElseSpeaking && !this._isReceiving) {
      this._haptic('rx');
      const speaker = channelUsers.find(u => u.speaking && !u.isSelf);
      if (speaker) this._notifySpeaking(speaker.name);
    }
    this._isReceiving = someoneElseSpeaking;
    this._setRX(someoneElseSpeaking);

    // Track last TX time for each user
    const now = Date.now();
    channelUsers.forEach(u => {
      if (u.speaking) this._lastTxTime[u.session] = now;
    });

    // Sort: speaking first, then by most recent TX, then self, then alphabetical
    const sorted = [...channelUsers].sort((a, b) => {
      if (a.speaking !== b.speaking) return a.speaking ? -1 : 1;
      const aTx = this._lastTxTime[a.session] || 0;
      const bTx = this._lastTxTime[b.session] || 0;
      if (aTx !== bTx) return bTx - aTx;
      if (a.isSelf !== b.isSelf) return a.isSelf ? -1 : 1;
      return a.name.localeCompare(b.name);
    });

    // Only show top 3
    const visible = sorted.slice(0, 3);

    // Update summary
    const speakingCount = channelUsers.filter(u => u.speaking).length;
    if (this.elements.userListSummary) {
      let summary = `${channelUsers.length} user${channelUsers.length !== 1 ? 's' : ''}`;
      if (speakingCount > 0) {
        summary += ` · ${speakingCount} talking`;
      }
      this.elements.userListSummary.textContent = summary;
    }

    // Auto-expand when speaking, auto-collapse 2s after silence
    if (speakingCount > 0) {
      if (this._collapseTimer) { clearTimeout(this._collapseTimer); this._collapseTimer = null; }
      this.elements.userList.classList.remove('collapsed');
      if (this.elements.userListChevron) this.elements.userListChevron.classList.add('open');
    } else if (this._isReceiving === false && !this.elements.userList.classList.contains('collapsed')) {
      // Was speaking, now stopped — collapse after delay
      if (!this._collapseTimer) {
        this._collapseTimer = setTimeout(() => {
          this.elements.userList.classList.add('collapsed');
          if (this.elements.userListChevron) this.elements.userListChevron.classList.remove('open');
          this._collapseTimer = null;
        }, 2000);
      }
    }

    visible.forEach(user => {
      const li = document.createElement('li');
      li.className = 'user-item';
      li.dataset.session = user.session;

      if (user.speaking) li.classList.add('speaking');
      if (user.isSelf) li.classList.add('self');

      // Avatar with initials
      const avatar = document.createElement('span');
      avatar.className = 'user-avatar';
      avatar.textContent = user.name.charAt(0).toUpperCase();
      li.appendChild(avatar);

      // Name
      const nameSpan = document.createElement('span');
      nameSpan.className = 'user-name';
      nameSpan.textContent = user.isSelf ? `${user.name} (you)` : user.name;
      li.appendChild(nameSpan);

      // Speaking indicator bars
      if (user.speaking) {
        const bars = document.createElement('span');
        bars.className = 'speaking-bars';
        for (let i = 0; i < 3; i++) {
          const bar = document.createElement('span');
          bar.className = 'bar';
          bars.appendChild(bar);
        }
        li.appendChild(bars);
      }

      this.elements.userList.appendChild(li);
    });
  }

  _setRadioStatus(state) {
    this.elements.radioIndicator.className = 'status-dot ' + state;
    // Reset label to just "Radio" for non-connected states
    const label = this.elements.radioIndicator.parentElement.querySelector('.status-label');
    if (label) label.textContent = 'Radio';
  }

  _setConnectionQuality(quality, rtt, jitter) {
    const dot = this.elements.radioIndicator;
    dot.className = 'status-dot quality-' + quality;
    // Show RTT and jitter inline next to "Radio" label
    const label = dot.parentElement.querySelector('.status-label');
    if (label) label.textContent = `Radio ${Math.round(rtt)}ms ±${Math.round(jitter)}ms`;
  }

  _startNetworkMonitoring() {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!conn) {
      console.log('[NET] NetworkInformation API not available');
      return;
    }
    const reportNetwork = () => {
      const type = conn.type || 'unknown';       // 'wifi', 'cellular'
      const effectiveType = conn.effectiveType;   // '4g', '3g'
      console.log('[NET]', type, effectiveType, conn.downlink + 'Mbps');
      // Report to backend for radio logs
      const apiBase = window.location.hostname === 'radio.livefusion.app'
        ? 'https://dashboard.livefusion.app/api'
        : '';
      fetch(apiBase + '/radio/network-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_token: this.username,
          network_type: type,
          effective_type: effectiveType,
          downlink_mbps: conn.downlink
        })
      }).catch(() => {});  // Best-effort, don't block
    };
    reportNetwork();
    conn.addEventListener('change', reportNetwork);
    this._networkCleanup = () => conn.removeEventListener('change', reportNetwork);
  }

  _setGPSStatus(state) {
    this.elements.gpsIndicator.className = 'status-dot ' + state;
  }

  _startGPSHealthPolling() {
    this._setGPSStatus('connecting');
    this._pollGPSHealth();
    this._gpsHealthInterval = setInterval(() => this._pollGPSHealth(), 30000);
  }

  _stopGPSHealthPolling() {
    if (this._gpsHealthInterval) {
      clearInterval(this._gpsHealthInterval);
      this._gpsHealthInterval = null;
    }
    this._setGPSStatus('disconnected');
  }

  async _pollGPSHealth() {
    try {
      const resp = await fetch('https://dashboard.livefusion.app/api/health');
      const data = await resp.json();
      this._setGPSStatus(data.gps === 'ok' ? 'connected' : 'disconnected');
    } catch {
      this._setGPSStatus('disconnected');
    }
  }

  _showConnectPanel() {
    this.elements.connectPanel.style.display = 'flex';
    this.elements.mainPanel.classList.add('hidden');
    this.elements.pttButton.disabled = true;
    // Hide map panel on desktop so it doesn't obscure the login screen
    const mapPanel = document.getElementById('map-panel');
    if (mapPanel) mapPanel.style.display = 'none';
  }

  _showMainPanel() {
    this.elements.connectPanel.style.display = 'none';
    this.elements.mainPanel.classList.remove('hidden');
    if (this.isMobile) {
      const ds = document.getElementById('device-section');
      if (ds) ds.style.display = 'none';
    }
    // Restore map panel on desktop
    const mapPanel = document.getElementById('map-panel');
    if (mapPanel) mapPanel.style.display = '';
  }

  async _registerServiceWorker() {
    if (!('serviceWorker' in navigator)) return;

    try {
      const reg = await navigator.serviceWorker.register('sw.js');
      console.log('[SW] Service worker registered');

      // Check for updates immediately
      reg.update().catch(() => {});

      // Check for updates every 60 seconds
      setInterval(() => reg.update().catch(() => {}), 60 * 1000);

      // When a new SW is waiting, tell it to activate immediately
      reg.addEventListener('updatefound', () => {
        const newWorker = reg.installing;
        if (!newWorker) return;

        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'activated') {
            console.log('[SW] New service worker activated - reloading');
            window.location.reload();
          }
        });
      });

      // If there's already a waiting worker, activate it
      if (reg.waiting) {
        reg.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
    } catch (err) {
      console.warn('[SW] Service worker registration failed:', err);
    }
  }

  /**
   * Trigger haptic feedback (vibration) if supported
   * @param {string} type - 'press' for PTT down, 'release' for PTT up, 'rx' for incoming
   */
  _haptic(type) {
    if (!navigator.vibrate) return;

    switch (type) {
      case 'press':
        navigator.vibrate(50); // Short firm tap
        break;
      case 'release':
        navigator.vibrate(30); // Lighter tap
        break;
      case 'rx':
        navigator.vibrate([20, 30, 20]); // Double-tap pattern for incoming
        break;
    }
  }
}

// Initialize app when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  try {
    // Initialize remote logging first
    initRemoteLogging();

    console.log('DOM loaded, initializing integrated PTT app...');
    const app = new PTTIntegratedApp();
    app.init();
    window.pttApp = app;
    console.log('Integrated PTT app initialized successfully');
  } catch (e) {
    console.error('Failed to initialize PTT app:', e);
    console.error('Error stack:', e.stack);
  }
});

// Catch any unhandled errors (log only — never block with alert)
window.addEventListener('error', (event) => {
  console.error('Global error:', event.error);
});

window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
});
